import {
  Button,
  NumberStepper,
  Select,
  Skeleton,
  formatNumber,
  formatSignedNumber,
} from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { Plus } from 'lucide-react';
import type { Location } from './inventory.ts';
import styles from './QuickAddStockForm.module.css';
import { MAX_ADJUSTMENT_QUANTITY } from './stock-adjustment.ts';
import type { StockRow } from './StockMatrix.tsx';
import { useQuickAddPlan } from './useQuickAddPlan.ts';
import { useRecordStock } from './useRecordStock.ts';
import { LoadFailed } from '../LoadFailed.tsx';
import { usePageContext } from '../usePageContext.ts';

export const QUICK_AMOUNTS = [1, 5, 10, 50] as const;

export type QuickAddStockFormProps = {
  readonly api: TypedClient;
  readonly rows: readonly StockRow[];
  readonly onRecorded: () => void;
};

type LineProps = {
  readonly row: StockRow;
  readonly onHand: number;
  readonly quantity: number;
  readonly active: boolean;
  readonly disabled: boolean;
  readonly onFocus: () => void;
  readonly onChange: (quantity: number) => void;
};

const QuickAddLine = ({ row, onHand, quantity, active, disabled, onFocus, onChange }: LineProps) => {
  const { t, locale } = usePageContext();
  return (
    <li className={styles['line']} data-active={active}>
      <span className={styles['variant']}>
        <strong>{row.label}</strong>
        <span className={styles['figures']}>
          {t('quickStock.onHand')} {formatNumber(onHand, locale)}
          {quantity === 0 ? null : (
            <span className={styles['after']}>
              {' → '}
              <strong>{formatNumber(onHand + quantity, locale)}</strong>
            </span>
          )}
        </span>
      </span>
      <NumberStepper
        label={row.label}
        hideLabel
        className={styles['stepper']}
        value={quantity}
        min={0}
        max={MAX_ADJUSTMENT_QUANTITY}
        disabled={disabled}
        decrementLabel={t('quickStock.decrement', { name: row.label })}
        incrementLabel={t('quickStock.increment', { name: row.label })}
        onFocus={onFocus}
        onValueChange={onChange}
      />
    </li>
  );
};

type QuickAmountsProps = {
  readonly target: string;
  readonly disabled: boolean;
  readonly onAdd: (amount: number) => void;
};

const QuickAmounts = ({ target, disabled, onAdd }: QuickAmountsProps) => {
  const { t, locale } = usePageContext();
  return (
    <div className={styles['chips']} role="group" aria-label={t('quickStock.quickAmounts')}>
      {QUICK_AMOUNTS.map((amount) => (
        <button
          key={amount}
          type="button"
          className={styles['chip']}
          disabled={disabled}
          aria-label={t('quickStock.addAmount', { count: amount, name: target })}
          onClick={() => {
            onAdd(amount);
          }}
        >
          {formatSignedNumber(amount, locale)}
        </button>
      ))}
    </div>
  );
};

type DepotChoiceProps = {
  readonly locations: readonly Location[];
  readonly value: string;
  readonly disabled: boolean;
  readonly onChoose: (locationId: string) => void;
};

const DepotChoice = ({ locations, value, disabled, onChoose }: DepotChoiceProps) => {
  const { t } = usePageContext();
  if (locations.length < 2) return null;
  return (
    <Select
      label={t('inventory.depot')}
      value={value}
      disabled={disabled}
      options={locations.map((location) => ({ value: location.id, label: location.name }))}
      onChange={(event) => {
        onChoose(event.target.value);
      }}
    />
  );
};

export const QuickAddStockForm = ({ api, rows, onRecorded }: QuickAddStockFormProps) => {
  const { t, locale, notify, describeError } = usePageContext();
  const plan = useQuickAddPlan(api, rows);
  const change = formatSignedNumber(plan.review.totalChange, locale);
  const submit = useRecordStock(api, () => {
    notify(t('quickStock.added', { change }));
    onRecorded();
  });
  const busy = submit.isPending;
  const ready = plan.review.lines.length > 0 && !busy;

  if (plan.stock.isError)
    return <LoadFailed description={describeError(plan.stock.error)} onRetry={plan.stock.refetch} />;
  if (plan.stock.isPending) return <Skeleton height="120px" />;
  return (
    <form
      className={styles['form']}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) submit.mutate(plan.review.lines);
      }}
    >
      <DepotChoice
        locations={plan.stock.locations}
        value={plan.locationId}
        disabled={busy}
        onChoose={plan.chooseDepot}
      />
      <ul className={styles['lines']} aria-label={t('quickStock.title')}>
        {plan.lines.map((line, position) => (
          <QuickAddLine
            key={line.row.variantId}
            row={line.row}
            onHand={line.onHand}
            quantity={line.quantity}
            active={position === plan.activeLine}
            disabled={busy}
            onFocus={() => {
              plan.setActiveLine(position);
            }}
            onChange={(quantity) => {
              plan.setQuantity(position, quantity);
            }}
          />
        ))}
      </ul>
      <QuickAmounts
        target={plan.lines[plan.activeLine]?.row.label ?? ''}
        disabled={busy}
        onAdd={plan.addToActiveLine}
      />
      {submit.error === null ? null : (
        <p role="alert" className={styles['error']}>
          {describeError(submit.error) ?? t('errors.UNKNOWN')}
        </p>
      )}
      <div className={styles['actions']}>
        <span className={styles['total']} role="status" data-empty={plan.review.totalChange === 0}>
          {t('quickStock.total', { count: plan.review.totalChange, change })}
        </span>
        <Button type="submit" icon={<Plus size={18} aria-hidden="true" />} disabled={!ready}>
          {busy ? t('common.saving') : t('quickStock.confirm')}
        </Button>
      </div>
    </form>
  );
};
